'use client';

import {type FC, useEffect} from 'react';
import {
	keepPreviousData,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query';
import clsx from 'clsx';
import Link from 'next/link';
import {useSearchParams} from 'next/navigation';

import {searchParamsToFilters} from '@/components/article-search-filters/search-params-filter';
import {Media} from '@/components/media';
import {PaginationWithLinks} from '@/components/pagination';
import {Skeleton} from '@/components/skeleton';
import type {ARTICLE_SEARCH_QUERY_RESULT} from '@/schemas/types';
import {documentToHref, formatDate} from '@/utils';
import {i18n} from '@/utils/i18n';

import {fetchArticles} from './fetchArticles';
import {ARTICLE_PAGE_SIZE} from './query';

interface Props {
	initialArticles?: ARTICLE_SEARCH_QUERY_RESULT;
	totalArticles?: number;
}

export const ArticleSearchListClient: FC<Props> = ({
	initialArticles,
	totalArticles,
}) => {
	const queryClient = useQueryClient();
	const searchParams = useSearchParams();

	const filters = searchParamsToFilters(searchParams);

	const page = parseInt(searchParams.get('page') || '1');

	const {status, data, error, isFetching, isPlaceholderData} = useQuery({
		queryKey: ['articles', page, filters.tema, filters.q],
		queryFn: () =>
			fetchArticles({
				page,
				categoryFilters: filters.tema,
				searchQuery: filters.q,
			}),
		placeholderData: keepPreviousData,
		staleTime: 5 * 60 * 1000, // 5 minutes
		initialData:
			page === 1 &&
			initialArticles &&
			totalArticles &&
			!filters.tema?.length &&
			!filters.q?.length
				? {
						articles: initialArticles,
						hasMore: initialArticles.length < totalArticles,
						totalArticles,
						nextPageArticlesLength: initialArticles.length,
					}
				: undefined,
	});

	// Prefetch the next page!
	useEffect(() => {
		if (!isPlaceholderData && data?.hasMore) {
			queryClient.prefetchQuery({
				queryKey: ['articles', page + 1, filters.tema, filters.q],
				queryFn: () =>
					fetchArticles({
						page: page + 1,
						categoryFilters: filters.tema,
						searchQuery: filters.q,
					}),
				staleTime: 5 * 60 * 1000, // 5 minutes
			});
		}
	}, [data, isPlaceholderData, page, queryClient, filters.q, filters.tema]);

	const nextPageArticlesLength = data?.nextPageArticlesLength ?? 10;

	return (
		<>
			{status === 'pending' || isFetching ? (
				<div className={clsx('grid', 'gap-10')}>
					{Array.from({length: nextPageArticlesLength}, (_, i) => (
						<ArticleCardSkeleton key={i} />
					))}
				</div>
			) : status === 'error' ? (
				<div>Error: {error.message}</div>
			) : (
				// `data` will either resolve to the latest page's data
				// or if fetching a new page, the last successful page's data

				<ul aria-label={i18n.components.articleSearch.articles}>
					{data?.articles?.length === 0 && (
						<div className={clsx('text-bodyMedium', 'italic')}>
							{i18n.components.articleSearch.noResults}
						</div>
					)}

					<span className="sr-only" aria-live="polite">
						{`${data?.articles?.length} resultater`}
					</span>

					{data?.articles?.map((article) => (
						<li key={article._id}>
							<article
								className={clsx(
									'grid',
									'grid-cols-1',
									'xl:grid-cols-2',
									'grid-rows-[max-content]',
									'relative',
									'border-b',
									'border-gray-300',
									'pb-8',
									'gap-8',
								)}
							>
								<div
									className={clsx(
										'pt-8',
										'h-full',
										'w-full',
										'relative',
									)}
								>
									<div
										className={clsx(
											'block',
											'text-h4',
											'hover:underline',
											'underline-offset-[6px]',
											'decoration-2',
											'aspect-video',
											'w-full',
											'md:h-full',
											'min-h-[75px]',
											'md:min-h-[110px]',
											'lg:min-h-[165px]',
											'rounded-[20px]',
											'overflow-hidden',
											'relative',
										)}
									>
										<div
											className={clsx(
												'absolute',
												'inset-0',
												'overflow-hidden',
											)}
										>
											<Media
												media={
													article.heading &&
													article.heading[0].media
												}
												disabledControls={true}
												disabledMask={true}
											/>
										</div>
									</div>
								</div>
								<div
									className={clsx(
										'pt-8',
										'flex',
										'flex-col',
										'gap-8',
									)}
								>
									<div>
										<Link
											href={documentToHref(
												article.category.slug?.current,
												article.slug?.current,
												article._type,
											)}
											className={clsx(
												'block',
												'text-h4',
												'hover:underline',
												'underline-offset-[6px]',
												'decoration-2',
												'mb-2',
											)}
										>
											<h2 className={clsx('text-h4')}>
												{article.title}
											</h2>
										</Link>
									</div>
									<div
										className={clsx(
											'text-lg',
											'leading-tight',
										)}
									>
										{article.heading?.at(0)?.tagline}
									</div>
									<div
										className={clsx(
											'flex',
											'gap-8',
											'items-center',
											'justify-between',
										)}
									>
										{article.category.title && (
											<div
												className={clsx(
													'text-center',
													'text-sm',
													'font-bold',
													'px-7',
													'py-2',
													'bg-green-100',
													'rounded-[10px]',
													'max-w-fit',
												)}
											>
												{article.category.title}
											</div>
										)}

										<div
											className={clsx(
												'leading-tight',
												'text-[10px]',
												'font-medium',
											)}
										>
											{
												i18n.components.articleSearch
													.published
											}
											:{' '}
											{formatDate(
												article.publishDate ??
													article._createdAt,
											)}
										</div>
									</div>
								</div>
							</article>
						</li>
					))}
				</ul>
			)}

			{data?.totalArticles ? (
				<div>
					<PaginationWithLinks
						page={page}
						pageSize={ARTICLE_PAGE_SIZE}
						totalCount={data.totalArticles}
					/>
				</div>
			) : null}
		</>
	);
};

const ArticleCardSkeleton = () => {
	return (
		<>
			<div
				className={clsx(
					'grid',
					'grid-cols-1',
					'xl:grid-cols-2',
					'grid-rows-[max-content]',
					'relative',
					'border-b',
					'border-gray-300',
					'pb-8',
					'gap-8',
				)}
			>
				<div className={clsx('pt-8', 'h-full', 'w-full', 'relative')}>
					{/* Image */}
					<Skeleton
						className={clsx(
							'aspect-[420/245]',
							'col-span-1',
							'mb-1',
							'rounded-[20px]',
						)}
					/>
				</div>
				<div className={clsx('pt-8', 'flex', 'flex-col', 'gap-8')}>
					<div>
						<div
							className={clsx(
								'block',
								'text-h4',
								'hover:underline',
								'underline-offset-[6px]',
								'decoration-2',
								'mb-2',
							)}
						>
							{/* Title */}
							<Skeleton
								className={clsx('h-9', 'w-4/5', 'mt-2')}
							/>
						</div>
					</div>
					<div className={clsx('text-lg', 'leading-tight')}>
						{/* Tagline */}
						<Skeleton className={clsx('h-5', 'w-full')} />
						<Skeleton className={clsx('h-5', 'w-full', 'mt-1')} />
						<Skeleton className={clsx('h-5', 'w-3/5', 'mt-1')} />
					</div>
					<div
						className={clsx(
							'flex',
							'gap-8',
							'items-center',
							'justify-between',
						)}
					>
						{/* Category */}
						<Skeleton className={clsx('h-9', 'w-[15ch]')} />

						<div
							className={clsx(
								'leading-tight',
								'text-[10px]',
								'font-medium',
							)}
						>
							{/* Date */}
							<Skeleton className={clsx('h-3', 'w-[10ch]')} />
						</div>
					</div>
				</div>
			</div>
		</>
	);
};
